import React from 'react';
import {
  HISTORY_DEPTH_MAX,
  HISTORY_DEPTH_MIN,
} from '../../constants/datasetGenerationLimits';
import {
  commitPositiveIntegerDraft,
  sanitizePositiveIntegerDraft,
} from '../../utils/numberInputDraft';
import './HistoryContextControl.css';

/** Same pill toggle as AgentConfigurator `AcBoolToggleRow` / MiniAgentConfigurator `RagToggleRow`. */
export function BoolToggleRow({
  on,
  onToggle,
  disabled,
  label,
}: {
  on: boolean;
  onToggle: () => void;
  disabled?: boolean;
  label: React.ReactNode;
}) {
  return (
    <div
      role="switch"
      aria-checked={on}
      className="history-context-control__switch-row"
      style={{
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={(e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
    >
      <div
        className="history-context-control__track"
        style={{
          background: on ? '#FF8300' : '#C6E0F7',
        }}
      >
        <div
          className="history-context-control__knob"
          style={{
            left: on ? 26 : 2,
          }}
        />
      </div>
      <span className="history-context-control__switch-label">{label}</span>
    </div>
  );
}

export interface HistoryContextControlProps {
  /** When true, only the last N turns are included; when false, the full dialog is used. */
  limited: boolean;
  onLimitedChange: (limited: boolean) => void;
  depthValue: string;
  onDepthValueChange: (value: string) => void;
  disabled?: boolean;
  depthMin?: number;
  depthMax?: number;
  depthInputId?: string;
  depthHintId?: string;
  /** Label next to the toggle (default: limit history depth). */
  toggleLabel?: React.ReactNode;
  /** Optional short hint under the toggle. */
  hint?: React.ReactNode;
  depthLabel?: string;
  depthHelperText?: React.ReactNode;
}

export const HistoryContextControl: React.FC<HistoryContextControlProps> = ({
  limited,
  onLimitedChange,
  depthValue,
  onDepthValueChange,
  disabled = false,
  depthMin = HISTORY_DEPTH_MIN,
  depthMax = HISTORY_DEPTH_MAX,
  depthInputId = 'history-context-depth',
  depthHintId,
  toggleLabel = 'Limit history depth',
  hint,
  depthLabel = 'Prior turns to include',
  depthHelperText,
}) => {
  return (
    <div className="history-context-control">
      <BoolToggleRow
        on={limited}
        onToggle={() => onLimitedChange(!limited)}
        disabled={disabled}
        label={toggleLabel}
      />
      {hint != null && hint !== false ? <p className="history-context-control__hint">{hint}</p> : null}
      {limited && (
        <div className="history-context-control__depth">
          <label className="history-context-control__depth-label" htmlFor={depthInputId}>
            {depthLabel}
          </label>
          <input
            id={depthInputId}
            type="number"
            className="history-context-control__depth-input"
            min={depthMin}
            max={depthMax}
            value={depthValue}
            onChange={(e) =>
              onDepthValueChange(sanitizePositiveIntegerDraft(e.target.value, depthMin, depthMax))
            }
            onBlur={(e) =>
              onDepthValueChange(commitPositiveIntegerDraft(e.target.value, depthMin, depthMax))
            }
            disabled={disabled}
            aria-describedby={depthHintId && depthHelperText != null ? depthHintId : undefined}
          />
          {depthHelperText != null ? (
            <p id={depthHintId} className="history-context-control__depth-hint">
              {depthHelperText}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default HistoryContextControl;
